import type { Stat } from '../../components/charts/StatTiles'
import { exactNumber } from '../../components/charts/scale'
import { NOT_MEASURED } from './fleetSummary'
import type { DeviceActivityRow, FleetCoverage } from '../../lib/api'

/**
 * The strip across the top of the Board: what is outstanding, and what is merely true.
 *
 * The Board replaces a dashboard of what the fleet has been doing with a board of what is
 * outstanding, and the ribbon is where that shows. Three tiles are problems and three are context,
 * and the split is the editorial choice: an operator opening this screen should be able to see in one
 * glance whether anything needs them.
 *
 * This is a module rather than six lines of markup for the same reason `fleetSummary.ts` is, and it
 * inherits that file's lesson wholesale. The dashboard once read a projection as
 * `topology?.deviceCount ?? 0` and chose its wording from `unprotected > 0`, so a projection that had
 * never been rebuilt, one that was stale, and an expired session all produced the same screen: **0
 * Devices** and the words *Every machine is covered* — an all-clear printed at the moment the page
 * knew least, beside a red banner saying the session had expired.
 *
 * The design specifies six hard figures. Three of their sources can fail to arrive, so:
 *
 *   null   the read did not arrive. A dash, a neutral tone, and **no note** - because every note
 *          here is a claim about the fleet, and a claim beside an unmeasured figure is the defect.
 *   []     the read arrived and counted nothing. A measured zero, which is worth saying plainly:
 *          "nothing failed to run" is good news and reads as good news.
 *
 * The reassuring note is the one most easily got wrong, which is why `ribbon.test.ts` asserts it
 * specifically: "Capability failures 0 — nothing failed to run" printed from a read that never
 * arrived is exactly the original defect wearing a new number.
 */
/**
 * The tones a tile takes, which are `Stat`'s own rather than a second vocabulary.
 *
 * `danger` is the design's "problem" tile and `plain` its neutral one. Reusing the existing words
 * means the ribbon and the fleet-summary tiles cannot come to mean different things by the same
 * color, which is the drift a parallel enum invites.
 */
export type RibbonTile = Stat

export interface RibbonSource {
  /** Null when the coverage read did not arrive. */
  coverage: FleetCoverage | null
  /** Null when the activity projection did not arrive; empty when it arrived and counted nothing. */
  activity: DeviceActivityRow[] | null
  /** Open cases needing a decision. Null when that read did not arrive. */
  openCases: number | null
  windowDays: number
}

/** A measured figure, or a dash. Never a zero standing in for an absent read. */
function figure(value: number | null): string {
  return value === null ? NOT_MEASURED : exactNumber(value)
}

export function ribbonTiles(source: RibbonSource): RibbonTile[] {
  const { coverage, activity, openCases, windowDays } = source

  const events = activity === null ? null : activity.reduce((sum, row) => sum + row.eventCount, 0)
  const failures = activity === null ? null : activity.reduce((sum, row) => sum + row.failureCount, 0)

  return [
    {
      label: 'Open cases',
      value: figure(openCases),
      // A note is a claim. "Need a decision" is only true of a number that was actually counted, and
      // only worth coloring when that number is above zero.
      note: openCases === null ? undefined : openCases > 0 ? 'need a decision' : 'nothing is waiting',
      tone: openCases !== null && openCases > 0 ? 'danger' : 'plain',
      to: '/cases'
    },
    {
      label: 'Carrying no policy',
      value: figure(coverage?.devicesCarryingNoPolicy ?? null),
      note: coverage === null ? undefined : `of ${exactNumber(coverage.devicesEnrolled)} devices`,
      tone: coverage !== null && coverage.devicesCarryingNoPolicy > 0 ? 'danger' : 'plain',
      to: '/devices'
    },
    {
      label: 'Logs no longer verify',
      value: figure(coverage?.auditChainsBroken ?? null),
      note:
        coverage === null
          ? undefined
          : coverage.auditChainsBroken > 0
            ? 'integrity risk'
            : 'every submitted log verifies',
      tone: coverage !== null && coverage.auditChainsBroken > 0 ? 'danger' : 'plain',
      to: '/devices'
    },
    {
      label: 'Devices reporting',
      value: figure(coverage?.devicesReporting ?? null),
      // The design says "all within the hour". The window is whatever the coverage read used, and
      // saying an hour when it was a day would be inventing a precision nobody measured.
      note: coverage === null ? undefined : `checked in within ${coverage.reportingWindowHours} hours`,
      tone: 'plain',
      to: '/devices'
    },
    {
      label: `Events · ${windowDays} d`,
      value: figure(events),
      note: events === null ? undefined : 'every check-in',
      tone: 'plain',
      to: '/cases?feed=raw'
    },
    {
      label: 'Capability failures',
      value: figure(failures),
      // The tile this module exists for. A measured zero earns the reassurance; an absent read earns
      // silence.
      note: failures === null ? undefined : failures > 0 ? 'ran and could not finish' : 'nothing failed to run',
      tone: failures !== null && failures > 0 ? 'danger' : 'plain',
      to: '/cases?feed=raw'
    }
  ]
}
